import { Navigate } from 'react-router-dom'
import { DiscoveryPreviewSection } from '@/components/home/discovery-preview-section'
import { PendingJudgeInvitations } from '@/components/home/pending-judge-invitations'
import { SignedInDashboard } from '@/components/home/signed-in-dashboard'
import { useSession } from '@/features/session/use-session'

/**
 * `/home` — "我的總覽" (My Overview), reachable via the signed-in-only nav
 * item in `root-layout.tsx`, and as `resolveSignedInHome`'s landing spot for
 * an authenticated account that holds no competition role at all (it can't
 * be sent to an admin/student/judge area it has no access to).
 *
 * PR-053 (v3.1.2, TASK-U-009): this route used to also serve anonymous
 * visitors (rendering the marketing hero + role cards for them, matching
 * `/`) because the signed-in nav's "首頁" used to link here. Now that "首頁"
 * always means `/` regardless of auth state, this route has no anonymous
 * audience left — an anonymous visitor here (e.g. a stale bookmark) is sent
 * to `/` instead of getting a second copy of the same public content.
 *
 * 2026 redesign (supersedes UX-045's per-role quick-action dashboard):
 * `SignedInDashboard` now aggregates every scoped role/relationship the
 * account holds into one "我的下一步" list instead of rendering role-gated
 * buttons — see `features/home/derive-home.ts` for the derivation this
 * page's single Acceptance Criterion rests on. `DiscoveryPreviewSection`
 * replaces the full `PublishedCompetitionsSection` explorer here (that one
 * still backs `/` and the "競賽" nav anchor) with a small, filter-free
 * preview — `/home` is a personal workspace, not a second competition
 * directory.
 */
export function HomePage() {
  const session = useSession()

  if (session.isPending) {
    return null
  }

  if (session.data?.authenticated !== true) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="flex flex-col gap-6">
      <PendingJudgeInvitations />
      <SignedInDashboard />
      <DiscoveryPreviewSection />
    </div>
  )
}
